export type Lang = "en" | "hi";

export interface LangPath {
  lang: Lang;
  path: string;
}

function normalise(pathname: string): string {
  const trimmed =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return trimmed === "" ? "/" : trimmed;
}

/** Split a pathname into its language and the language-free route. */
export function langFromPath(pathname: string): LangPath {
  const path = normalise(pathname);
  if (path === "/hi") {
    return { lang: "hi", path: "/" };
  }
  if (path.startsWith("/hi/")) {
    return { lang: "hi", path: normalise(path.slice(3)) };
  }
  return { lang: "en", path };
}

/** The same route in another language. */
export function localePath(pathname: string, lang: Lang): string {
  const { path } = langFromPath(pathname);
  if (lang === "en") {
    return path;
  }
  return path === "/" ? "/hi" : `/hi${path}`;
}

/**
 * A nav href for rendering: a same-page hash link (`/#about`) keeps its
 * fragment but gains the language prefix; every other href is passed
 * through `localePath` as usual.
 */
export function navHref(href: string, lang: Lang): string {
  if (href.startsWith("/#")) {
    return `${localePath("/", lang)}${href.slice(1)}`;
  }
  return localePath(href, lang);
}

/**
 * The href a link primitive should render: an internal route keeps the
 * visitor in their current language, an external link (or a non-route href
 * like a `mailto:`/`tel:`) is returned untouched. The language is read from
 * the current URL so shared primitives (`Button`, `Tile`, ...) localise on
 * their own without every caller threading `lang` through. Idempotent: a
 * path that already carries `/hi` stays as it is.
 */
export function localizedHref(href: string, external?: boolean): string {
  if (external || !href.startsWith("/") || href.startsWith("//")) {
    return href;
  }
  const { lang } = langFromPath(window.location.pathname);
  return navHref(href, lang);
}

/** Human label for the language toggle. */
export const LANG_LABEL = {
  en: "English",
  hi: "हिन्दी",
} satisfies Record<Lang, string>;
